import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { AlertTriangle, Trash2 } from "lucide-react"

import {
  Button,
  Checkbox,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@f2e/ui/components/base"
import { DialogContentPlus } from "@f2e/ui/components/base-plus"

import type { UnifiedTool } from "./type"
import { InvocationInstructionField } from "./InvocationInstructionField"
import {
  HttpRequestInputContracts,
  HttpRequestInputFormatSelect,
} from "./HttpRequestInputContracts"
import {
  collectHttpAgentInputs,
  getHttpInputContractErrors,
} from "@/lib/httpRequestInputs"
import {
  HttpRequestVariableInput,
  type ContextVariableOption,
} from "./HttpRequestVariableInput"
import type {
  HttpRequestAuthType,
  HttpRequestBodyFieldType,
  HttpRequestMethod,
  HttpRequestToolConfig,
} from "@/types/httpRequestTool"

export interface HttpRequestToolModalProps {
  tool: UnifiedTool
  disabled?: boolean
  onUpdateTool: (toolId: string, updates: Partial<UnifiedTool>) => void
  onInvalidSelectionChange: (toolId: string, hasInvalid: boolean) => void
}

const DEFAULT_HTTP_REQUEST: HttpRequestToolConfig = {
  method: "GET",
  urlTemplate: "https://",
  auth: { type: "none" },
  headers: [],
  queryParams: [],
  body: { template: undefined },
}

const authTypes: HttpRequestAuthType[] = [
  "none",
  "bearer",
  "apiKey",
  "basic",
  "customHeader",
]

const bodyFieldTypes: HttpRequestBodyFieldType[] = [
  "static",
  "context",
  "agent",
]

const contextVariableOptions: ContextVariableOption[] = [
  {
    value: "{{user.email}}",
    labelKey:
      "workspace.agent.basicForm.unifiedToolSelector.httpRequestContextOption.userEmail",
  },
  {
    value: "{{user.id}}",
    labelKey:
      "workspace.agent.basicForm.unifiedToolSelector.httpRequestContextOption.userId",
  },
  {
    value: "{{user.external_id}}",
    labelKey:
      "workspace.agent.basicForm.unifiedToolSelector.httpRequestContextOption.userExternalId",
  },
] as const

const httpMethods: HttpRequestMethod[] = [
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
  "HEAD",
]

const mutatingHttpMethods = new Set<HttpRequestMethod>([
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
])

type BodyEditorMode = "ui" | "text"

interface BodyUiField {
  key: string
  type: HttpRequestBodyFieldType
  value: string
}

const cloneConfig = (config?: HttpRequestToolConfig): HttpRequestToolConfig => {
  const cloneTemplate =
    config?.body?.template === undefined
      ? undefined
      : JSON.parse(JSON.stringify(config.body.template))

  if (!config) return { ...DEFAULT_HTTP_REQUEST }
  return {
    ...DEFAULT_HTTP_REQUEST,
    ...config,
    auth: { ...DEFAULT_HTTP_REQUEST.auth, ...config.auth },
    headers: [...(config.headers ?? [])],
    queryParams: [...(config.queryParams ?? [])],
    body: {
      ...DEFAULT_HTTP_REQUEST.body,
      ...config.body,
      template: cloneTemplate,
    },
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const AGENT_PLACEHOLDER_RE = /^\{\{\s*agent\[(.*?)\]\s*\}\}$/

const toFieldValue = (field: BodyUiField): string => {
  if (field.type === "agent") return `{{agent[${field.value}]}}`
  return field.value
}

const isContextPlaceholder = (value: string): boolean =>
  contextVariableOptions.some((option) => option.value === value)

const toUiTemplateObject = (
  template: unknown
): Record<string, unknown> | null => {
  if (template === undefined) return {}
  return isPlainObject(template) ? template : null
}

const toBodyUiField = (key: string, value: unknown): BodyUiField | null => {
  if (typeof value !== "string") return null

  const agentMatch = value.match(AGENT_PLACEHOLDER_RE)
  const inputMatches = collectHttpAgentInputs(value)
  if (agentMatch && inputMatches.length === 1 && inputMatches[0].wholeValue) {
    return {
      key,
      type: "agent",
      value: agentMatch[1]?.trim() ?? "",
    }
  }

  if (isContextPlaceholder(value)) {
    return {
      key,
      type: "context",
      value,
    }
  }

  return {
    key,
    type: "static",
    value,
  }
}

const templateToUiFields = (template: unknown): BodyUiField[] | null => {
  const templateObject = toUiTemplateObject(template)
  if (!templateObject) return null

  const fields: BodyUiField[] = []
  for (const [key, value] of Object.entries(templateObject)) {
    const field = toBodyUiField(key, value)
    if (!field) return null
    fields.push(field)
  }
  return fields
}

const uiFieldsToTemplate = (fields: BodyUiField[]): Record<string, string> => {
  const next: Record<string, string> = {}
  for (const field of fields) {
    if (!field.key) continue
    next[field.key] = toFieldValue(field)
  }
  return next
}

const stringifyTemplate = (template: unknown): string => {
  if (template === undefined) return "{}"
  try {
    return JSON.stringify(template, null, 2)
  } catch {
    return "{}"
  }
}

const TEMPLATE_TEXT_PLACEHOLDER = `{
  "email": "{{user.email}}",
  "start_date": "{{agent[fill in the start date]}}"
}`

const parseBodyText = (text: string): unknown => {
  if (!text.trim()) return {}
  try {
    const parsed = JSON.parse(text)
    return parsed
  } catch {
    return undefined
  }
}

const nextDefaultFieldKey = (fields: BodyUiField[]): string => {
  let index = fields.length + 1
  let candidate = `field_${index}`
  const existingKeys = new Set(fields.map((field) => field.key))
  while (existingKeys.has(candidate)) {
    index += 1
    candidate = `field_${index}`
  }
  return candidate
}

const hasMissingAuthFields = (config: HttpRequestToolConfig): boolean => {
  switch (config.auth.type) {
    case "bearer":
      return !config.auth.token
    case "apiKey":
      return (
        !config.auth.apiKey ||
        !config.auth.apiKeyParamName ||
        !config.auth.apiKeyLocation
      )
    case "basic":
      return !config.auth.username || !config.auth.password
    case "customHeader":
      return !config.auth.headerName || !config.auth.headerValue
    default:
      return false
  }
}

export const HttpRequestToolModal: React.FC<HttpRequestToolModalProps> = ({
  tool,
  disabled = false,
  onUpdateTool,
  onInvalidSelectionChange,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [local, setLocal] = useState<HttpRequestToolConfig>(
    cloneConfig(tool.httpRequest)
  )
  const localRef = useRef(local)
  const [bodyEditorMode, setBodyEditorMode] = useState<BodyEditorMode>("ui")
  const [bodyText, setBodyText] = useState("{}")
  const [bodyTextInvalid, setBodyTextInvalid] = useState(false)
  const [keyInputDrafts, setKeyInputDrafts] = useState<Record<number, string>>(
    {}
  )
  const [editorModeHint, setEditorModeHint] = useState<string | null>(null)
  const [aiDraftPolicyReset, setAIDraftPolicyReset] = useState(false)

  const isCopilotTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    return Boolean(
      target.closest("[data-copilot-panel],[data-copilot-launcher]")
    )
  }

  useEffect(() => {
    setBodyEditorMode("ui")
    setBodyText("{}")
    setBodyTextInvalid(false)
    setKeyInputDrafts({})
    setEditorModeHint(null)
    setAIDraftPolicyReset(false)
  }, [tool.id])

  useEffect(() => {
    const next = cloneConfig(tool.httpRequest)
    localRef.current = next
    setLocal(next)
    if (bodyEditorMode !== "text") {
      const nextText = stringifyTemplate(next.body.template)
      setBodyText(nextText)
      setBodyTextInvalid(parseBodyText(nextText) === undefined)
    }
    setEditorModeHint(null)

    if (!tool.httpRequest) {
      onUpdateTool(tool.id, { httpRequest: next })
    }
  }, [bodyEditorMode, onUpdateTool, tool.httpRequest, tool.id])

  const bodyUiFields = useMemo(
    () => templateToUiFields(local.body.template),
    [local.body.template]
  )

  const agentInputs = useMemo(
    () => collectHttpAgentInputs(local.body.template),
    [local.body.template]
  )

  const hasInvalid = useMemo(() => {
    if (!tool.name || !tool.name.trim()) return true
    if (hasMissingAuthFields(local)) return true
    if (bodyTextInvalid) return true
    if (
      getHttpInputContractErrors(local.body.template, local.body.inputContracts)
        .length
    )
      return true
    if (bodyEditorMode === "ui" && !bodyUiFields) return false
    const fields = bodyUiFields ?? []
    const hasEmptyDraftKey = Object.values(keyInputDrafts).some(
      (value) => value.trim().length === 0
    )
    if (hasEmptyDraftKey) return true
    const keys = fields.map((field) => field.key.trim()).filter(Boolean)
    if (new Set(keys).size !== keys.length) return true
    return fields.some(
      (field) =>
        !field.key.trim() || (field.type === "agent" && !field.value.trim())
    )
  }, [
    bodyEditorMode,
    bodyTextInvalid,
    bodyUiFields,
    keyInputDrafts,
    local,
    tool.name,
  ])

  useEffect(() => {
    onInvalidSelectionChange(tool.id, hasInvalid)
  }, [hasInvalid, onInvalidSelectionChange, tool.id])

  const updateConfig = (
    updater: (prev: HttpRequestToolConfig) => HttpRequestToolConfig
  ) => {
    const next = updater(localRef.current)
    const resetAIDraftPolicy = tool.draftPolicy === "auto"
    localRef.current = next
    setLocal(next)
    onUpdateTool(tool.id, {
      httpRequest: next,
      ...(resetAIDraftPolicy
        ? { draftPolicy: "approval_required" as const }
        : {}),
    })
    if (resetAIDraftPolicy) setAIDraftPolicyReset(true)
  }

  const updateHeaders = (next: HttpRequestToolConfig["headers"]) =>
    updateConfig((prev) => ({ ...prev, headers: next ?? [] }))

  const updateQueryParams = (next: HttpRequestToolConfig["queryParams"]) =>
    updateConfig((prev) => ({ ...prev, queryParams: next ?? [] }))

  const showUiTemplateFields = bodyEditorMode === "ui" && bodyUiFields !== null

  const updateBodyTemplate = (template: unknown) =>
    updateConfig((prev) => ({
      ...prev,
      body: { ...prev.body, template },
    }))

  const updateBodyUiFields = (nextFields: BodyUiField[]) => {
    const nextTemplate = uiFieldsToTemplate(nextFields)
    updateBodyTemplate(nextTemplate)
    setBodyText(JSON.stringify(nextTemplate, null, 2))
    setBodyTextInvalid(false)
    setEditorModeHint(null)
  }

  const removeDraftAtIndex = (index: number) => {
    setKeyInputDrafts((prev) => {
      if (!(index in prev)) return prev
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const shiftDraftsAfterRemove = (removedIndex: number) => {
    setKeyInputDrafts((prev) => {
      const next: Record<number, string> = {}
      for (const [rawIndex, value] of Object.entries(prev)) {
        const index = Number(rawIndex)
        if (index < removedIndex) next[index] = value
        if (index > removedIndex) next[index - 1] = value
      }
      return next
    })
  }

  const handleContextChange = (index: number, nextValue: string) => {
    if (!bodyUiFields) return
    const next = [...bodyUiFields]
    next[index] = { ...next[index], value: nextValue }
    updateBodyUiFields(next)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen} modal={false}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        {t(
          "workspace.agent.basicForm.unifiedToolSelector.httpRequestConfigure"
        )}
      </Button>
      <DialogContentPlus
        className="max-w-4xl w-[92vw] max-h-[85vh] overflow-auto p-0"
        onInteractOutside={(event) => {
          if (isCopilotTarget(event.target)) {
            event.preventDefault()
          }
        }}
      >
        <DialogHeader className="sticky top-0 z-10 bg-white dark:bg-slate-950 border-b px-6 py-4">
          <DialogTitle className="text-base sm:text-lg">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.httpRequestTitle"
            )}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.httpRequestDescription"
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 py-4 space-y-6">
          <InvocationInstructionField
            id={`http-invocation-${tool.id}`}
            label={t(
              "workspace.agent.basicForm.unifiedToolSelector.httpRequestInvocation"
            )}
            value={tool.invocationInstruction ?? ""}
            placeholder={t(
              "workspace.agent.basicForm.unifiedToolSelector.httpRequestInvocationPlaceholder"
            )}
            onChange={(value) =>
              onUpdateTool(tool.id, { invocationInstruction: value })
            }
            disabled={disabled}
            rows={4}
          />

          <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id={`http-request-ai-draft-${tool.id}`}
                checked={tool.draftPolicy === "auto"}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  setAIDraftPolicyReset(false)
                  onUpdateTool(tool.id, {
                    draftPolicy:
                      checked === true ? "auto" : "approval_required",
                  })
                }}
              />
              <div className="space-y-1">
                <Label
                  htmlFor={`http-request-ai-draft-${tool.id}`}
                  className="text-sm font-medium"
                >
                  {t(
                    "workspace.agent.basicForm.unifiedToolSelector.httpRequestAIDraftLabel"
                  )}
                </Label>
                <p className="max-w-[70ch] text-xs text-muted-foreground">
                  {t(
                    tool.draftPolicy === "auto"
                      ? "workspace.agent.basicForm.unifiedToolSelector.httpRequestAIDraftEnabledHelp"
                      : "workspace.agent.basicForm.unifiedToolSelector.httpRequestAIDraftDisabledHelp"
                  )}
                </p>
              </div>
            </div>
            {aiDraftPolicyReset && (
              <div
                role="status"
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
              >
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  {t(
                    "workspace.agent.basicForm.unifiedToolSelector.httpRequestAIDraftResetWarning"
                  )}
                </span>
              </div>
            )}
            {mutatingHttpMethods.has(local.method) && (
              <div
                role="status"
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
              >
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  {t(
                    "workspace.agent.basicForm.unifiedToolSelector.httpRequestAIDraftMutationWarning",
                    { method: local.method }
                  )}
                </span>
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestName"
                )}
              </Label>
              <Input
                value={tool.name ?? ""}
                placeholder={t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestNamePlaceholder"
                )}
                onChange={(e) =>
                  onUpdateTool(tool.id, { name: e.target.value })
                }
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestMethod"
                )}
              </Label>
              <Select
                value={local.method}
                onValueChange={(value) =>
                  updateConfig((prev) => ({
                    ...prev,
                    method: value as HttpRequestMethod,
                  }))
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {httpMethods.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.httpRequestUrl"
              )}
            </Label>
            <Input
              value={local.urlTemplate}
              placeholder="https://api.example.com/orders?email={{user.email}}"
              onChange={(e) =>
                updateConfig((prev) => ({
                  ...prev,
                  urlTemplate: e.target.value,
                }))
              }
              disabled={disabled}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.httpRequestUrlHelp"
              )}
            </p>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.httpRequestAuth"
              )}
            </Label>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                value={local.auth.type}
                onValueChange={(value) =>
                  updateConfig((prev) => ({
                    ...prev,
                    auth: { ...prev.auth, type: value as HttpRequestAuthType },
                  }))
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {authTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(
                        `workspace.agent.basicForm.unifiedToolSelector.httpRequestAuthType.${type}`
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {local.auth.type === "bearer" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t(
                    "workspace.agent.basicForm.unifiedToolSelector.httpRequestBearerToken"
                  )}
                </Label>
                <Input
                  type="password"
                  value={local.auth.token ?? ""}
                  onChange={(e) =>
                    updateConfig((prev) => ({
                      ...prev,
                      auth: { ...prev.auth, token: e.target.value },
                    }))
                  }
                  disabled={disabled}
                />
              </div>
            )}

            {local.auth.type === "apiKey" && (
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestApiKey"
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={local.auth.apiKey ?? ""}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        auth: { ...prev.auth, apiKey: e.target.value },
                      }))
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestApiKeyParam"
                    )}
                  </Label>
                  <Input
                    value={local.auth.apiKeyParamName ?? ""}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        auth: { ...prev.auth, apiKeyParamName: e.target.value },
                      }))
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestApiKeyLocation"
                    )}
                  </Label>
                  <Select
                    value={local.auth.apiKeyLocation ?? "header"}
                    onValueChange={(value) =>
                      updateConfig((prev) => ({
                        ...prev,
                        auth: {
                          ...prev.auth,
                          apiKeyLocation: value as "header" | "query",
                        },
                      }))
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="header">
                        {t(
                          "workspace.agent.basicForm.unifiedToolSelector.httpRequestApiKeyHeader"
                        )}
                      </SelectItem>
                      <SelectItem value="query">
                        {t(
                          "workspace.agent.basicForm.unifiedToolSelector.httpRequestApiKeyQuery"
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {local.auth.type === "basic" && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestUsername"
                    )}
                  </Label>
                  <Input
                    value={local.auth.username ?? ""}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        auth: { ...prev.auth, username: e.target.value },
                      }))
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestPassword"
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={local.auth.password ?? ""}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        auth: { ...prev.auth, password: e.target.value },
                      }))
                    }
                    disabled={disabled}
                  />
                </div>
              </div>
            )}

            {local.auth.type === "customHeader" && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestHeaderName"
                    )}
                  </Label>
                  <Input
                    value={local.auth.headerName ?? ""}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        auth: { ...prev.auth, headerName: e.target.value },
                      }))
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestHeaderValue"
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={local.auth.headerValue ?? ""}
                    onChange={(e) =>
                      updateConfig((prev) => ({
                        ...prev,
                        auth: { ...prev.auth, headerValue: e.target.value },
                      }))
                    }
                    disabled={disabled}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestHeaders"
                )}
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={() =>
                  updateHeaders([
                    ...(local.headers ?? []),
                    { key: "", value: "" },
                  ])
                }
              >
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestAddHeader"
                )}
              </Button>
            </div>
            {(local.headers ?? []).length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestHeadersEmpty"
                )}
              </p>
            )}
            <div className="space-y-2">
              {(local.headers ?? []).map((header, idx) => (
                <div key={`header-${idx}`} className="flex items-center gap-2">
                  <Input
                    value={header.key}
                    placeholder="Header"
                    onChange={(e) => {
                      const next = [...(local.headers ?? [])]
                      next[idx] = { ...next[idx], key: e.target.value }
                      updateHeaders(next)
                    }}
                    disabled={disabled}
                  />
                  <Input
                    value={header.value}
                    placeholder="Value"
                    onChange={(e) => {
                      const next = [...(local.headers ?? [])]
                      next[idx] = { ...next[idx], value: e.target.value }
                      updateHeaders(next)
                    }}
                    disabled={disabled}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const next = [...(local.headers ?? [])]
                      next.splice(idx, 1)
                      updateHeaders(next)
                    }}
                    disabled={disabled}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestQueryParams"
                )}
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={() =>
                  updateQueryParams([
                    ...(local.queryParams ?? []),
                    { key: "", value: "" },
                  ])
                }
              >
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestAddQuery"
                )}
              </Button>
            </div>
            {(local.queryParams ?? []).length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestQueryEmpty"
                )}
              </p>
            )}
            <div className="space-y-2">
              {(local.queryParams ?? []).map((param, idx) => (
                <div key={`query-${idx}`} className="flex items-center gap-2">
                  <Input
                    value={param.key}
                    placeholder="Key"
                    onChange={(e) => {
                      const next = [...(local.queryParams ?? [])]
                      next[idx] = { ...next[idx], key: e.target.value }
                      updateQueryParams(next)
                    }}
                    disabled={disabled}
                  />
                  <Input
                    value={param.value}
                    placeholder="Value"
                    onChange={(e) => {
                      const next = [...(local.queryParams ?? [])]
                      next[idx] = { ...next[idx], value: e.target.value }
                      updateQueryParams(next)
                    }}
                    disabled={disabled}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const next = [...(local.queryParams ?? [])]
                      next.splice(idx, 1)
                      updateQueryParams(next)
                    }}
                    disabled={disabled}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.httpRequestBody"
              )}
            </Label>
            <div className="inline-flex rounded-md border border-input bg-background p-1">
              <Button
                type="button"
                size="sm"
                variant={bodyEditorMode === "ui" ? "default" : "ghost"}
                disabled={disabled}
                onClick={() => {
                  setBodyEditorMode("ui")
                  if (bodyUiFields === null) {
                    setEditorModeHint(
                      t(
                        "workspace.agent.basicForm.unifiedToolSelector.httpRequestBodyEditorModeUiUnavailable"
                      )
                    )
                    return
                  }
                  setEditorModeHint(null)
                }}
              >
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestBodyEditorModeUi"
                )}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={bodyEditorMode === "text" ? "default" : "ghost"}
                disabled={disabled}
                onClick={() => {
                  setBodyEditorMode("text")
                  setEditorModeHint(null)
                }}
              >
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestBodyEditorModeText"
                )}
              </Button>
            </div>
            {editorModeHint &&
              !(bodyEditorMode === "ui" && !showUiTemplateFields) && (
                <p className="text-xs text-amber-600 dark:text-amber-300">
                  {editorModeHint}
                </p>
              )}

            {bodyEditorMode === "text" && (
              <div className="space-y-2">
                <Textarea
                  value={bodyText}
                  placeholder={TEMPLATE_TEXT_PLACEHOLDER}
                  onChange={(e) => {
                    const value = e.target.value
                    setBodyText(value)
                    const parsed = parseBodyText(value)
                    if (parsed === undefined) {
                      setBodyTextInvalid(true)
                      return
                    }
                    updateBodyTemplate(parsed)
                    setBodyTextInvalid(false)
                    setEditorModeHint(null)
                  }}
                  disabled={disabled}
                  className="min-h-[140px] font-mono text-xs"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t(
                    "workspace.agent.basicForm.unifiedToolSelector.httpRequestBodyTemplateTextHelp"
                  )}
                </p>
                {bodyTextInvalid && (
                  <p className="text-xs text-red-600 dark:text-red-300">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestBodyTemplateJsonInvalid"
                    )}
                  </p>
                )}
              </div>
            )}

            {bodyEditorMode === "ui" && !showUiTemplateFields && (
              <p className="text-xs text-amber-600 dark:text-amber-300">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequestBodyEditorModeUiUnavailable"
                )}
              </p>
            )}

            {bodyEditorMode === "ui" && showUiTemplateFields && (
              <div className="space-y-3">
                <div className="flex items-center justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    onClick={() =>
                      updateBodyUiFields([
                        ...bodyUiFields,
                        {
                          key: nextDefaultFieldKey(bodyUiFields),
                          type: "static",
                          value: "",
                        },
                      ])
                    }
                  >
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestAddField"
                    )}
                  </Button>
                </div>
                {bodyUiFields.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestBodyEmpty"
                    )}
                  </p>
                )}
                <div className="space-y-2">
                  {bodyUiFields.map((field, idx) => {
                    const agentInput = agentInputs.find(
                      (input) => input.path === field.key && input.wholeValue
                    )
                    return (
                      <div
                        key={`body-field-${idx}`}
                        className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 sm:grid-cols-[minmax(8rem,0.8fr)_minmax(0,1.8fr)_10rem_auto] items-center"
                      >
                        <Input
                          value={keyInputDrafts[idx] ?? field.key}
                          placeholder={t(
                            "workspace.agent.basicForm.unifiedToolSelector.httpRequestFieldKey"
                          )}
                          onChange={(e) => {
                            const nextKey = e.target.value
                            setKeyInputDrafts((prev) => ({
                              ...prev,
                              [idx]: nextKey,
                            }))
                            if (!nextKey.trim()) return
                            const next = [...bodyUiFields]
                            next[idx] = { ...next[idx], key: nextKey }
                            updateBodyUiFields(next)
                            removeDraftAtIndex(idx)
                          }}
                          disabled={disabled}
                          className="col-span-2 sm:col-span-1"
                        />
                        <div
                          className={`col-span-2 min-w-0 flex items-center gap-3 rounded-md border border-input bg-background px-3 py-1.5 ${field.type !== "agent" || !agentInput ? "sm:col-span-2" : "sm:col-span-1"}`}
                        >
                          <Select
                            value={field.type}
                            onValueChange={(value) => {
                              const next = [...bodyUiFields]
                              const nextType = value as HttpRequestBodyFieldType
                              const defaultContextValue =
                                nextType === "context"
                                  ? (contextVariableOptions[0]?.value ?? "")
                                  : ""
                              next[idx] = {
                                ...next[idx],
                                type: nextType,
                                value:
                                  nextType === "context"
                                    ? defaultContextValue
                                    : "",
                              }
                              updateBodyUiFields(next)
                            }}
                            disabled={disabled}
                          >
                            <SelectTrigger className="h-auto border-0 bg-transparent p-0 shadow-none focus:ring-0">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {bodyFieldTypes.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {t(
                                    `workspace.agent.basicForm.unifiedToolSelector.httpRequestFieldType.${type}`
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {field.type === "agent" ? (
                            <Input
                              value={field.value}
                              placeholder={t(
                                "workspace.agent.basicForm.unifiedToolSelector.httpRequestFieldLabelPlaceholder"
                              )}
                              maxLength={100}
                              onChange={(e) => {
                                const next = [...bodyUiFields]
                                next[idx] = {
                                  ...next[idx],
                                  value: e.target.value,
                                }
                                updateBodyUiFields(next)
                              }}
                              disabled={disabled}
                              className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
                            />
                          ) : field.type === "context" ? (
                            <HttpRequestVariableInput
                              value={field.value}
                              disabled={disabled}
                              options={contextVariableOptions}
                              onChange={(nextValue) =>
                                handleContextChange(idx, nextValue)
                              }
                            />
                          ) : (
                            <Input
                              value={field.value}
                              placeholder={t(
                                "workspace.agent.basicForm.unifiedToolSelector.httpRequestFieldValue"
                              )}
                              onChange={(e) => {
                                const next = [...bodyUiFields]
                                next[idx] = {
                                  ...next[idx],
                                  value: e.target.value,
                                }
                                updateBodyUiFields(next)
                              }}
                              disabled={disabled}
                              className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
                            />
                          )}
                        </div>
                        {field.type === "agent" && agentInput ? (
                          <HttpRequestInputFormatSelect
                            field={agentInput}
                            contract={
                              local.body.inputContracts?.[agentInput.key]
                            }
                            disabled={disabled}
                            onChange={(contract) =>
                              updateConfig((prev) => ({
                                ...prev,
                                body: {
                                  ...prev.body,
                                  inputContracts: {
                                    ...prev.body.inputContracts,
                                    [agentInput.key]: contract,
                                  },
                                },
                              }))
                            }
                          />
                        ) : (
                          <span className="sm:hidden" />
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const next = [...bodyUiFields]
                            next.splice(idx, 1)
                            updateBodyUiFields(next)
                            shiftDraftsAfterRemove(idx)
                          }}
                          disabled={disabled}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t(
                    "workspace.agent.basicForm.unifiedToolSelector.httpRequestBodyFieldsHelp"
                  )}
                </p>
              </div>
            )}
          </div>

          <HttpRequestInputContracts
            template={local.body.template}
            contracts={local.body.inputContracts}
            disabled={disabled || bodyTextInvalid}
            showFields={!showUiTemplateFields}
            onChange={(inputContracts) =>
              updateConfig((prev) => ({
                ...prev,
                body: { ...prev.body, inputContracts },
              }))
            }
          />

          {hasInvalid && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.httpRequestValidationError"
              )}
            </div>
          )}
        </div>
      </DialogContentPlus>
    </Dialog>
  )
}
